import type {
  AssetTypeControllerFindAllRequest,
  SubsystemControllerFindAllRequest,
  ComponentClassControllerFindAllRequest,
  EventStateControllerFindAllRequest,
  ParameterNameControllerFindAllRequest,
  ComponentTypeControllerFindAllRequest,
  AssetTypeResponseDto,
  SubsystemResponseDto,
  ComponentClassResponseDto,
  AssetTypeSubsystemMappingResponseDto,
  GetAllMappingsRes,
  PaginationMetaDto,
  ListComponentTypesResponseDto,
} from '@evimo/client';
import type { LookupRow } from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import type {
  EnrichedSubsystem,
  EnrichedComponentClass,
  EnrichedAssetType,
  ComponentTypeResponseDto,
} from '~/app/services/knowledgeBase/types';
import {
  assetTypesApi,
  subsystemsApi,
  componentClassesApi,
  eventStatesApi,
  parameterNamesApi,
  componentTypeApi,
  assetSubsystemMappingsApi,
  assetSubsystemComponentClassMappingsApi,
  assetSubsystemComponentClassComponentTypeMappingsApi,
} from '~/app/services/knowledgeBase/api-instances';
import {
  mapAssetType,
  mapSubsystem,
  mapComponentClassWithLookup,
  mapEventState,
  mapParameterName,
  mapComponentType,
  mapAssetSubsystemComponentClass,
  mapAssetSubsystemComponentClassComponentType,
  mapComponentClass,
  mapComponentTypeWithLookup,
  mapManufacturer,
  mapAssetModel,
  mapAssetTypeManufacturerModel,
} from '~/app/services/knowledgeBase/mappers';
import {
  AssetModelCatalogApi,
  AssetTypeManufacturerModelCatalogApi,
  ManufacturerCatalogApi,
} from '~/app/services/knowledgeBase/catalog.api';

async function getAssetTypeLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: AssetTypeControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await assetTypesApi.assetTypeControllerFindAll(_params);
  const items = res.data || [];
  const enriched = await Promise.all(
    items.map(async (a: AssetTypeResponseDto) => {
      const mappings =
        await assetSubsystemMappingsApi.assetSubsystemMappingControllerGetSubsystemsForAssetType(
          { assetTypeId: a.id },
        );
      const subsystemIds = mappings.map(
        (s: AssetTypeSubsystemMappingResponseDto) => s.subsystemId,
      );
      return { ...a, subsystemIds } as EnrichedAssetType;
    }),
  );
  return enriched.map(mapAssetType);
}

async function getSubsystemLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: SubsystemControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await subsystemsApi.subsystemControllerFindAll(_params);
  const items = res.data || [];
  const enriched = await Promise.all(
    items.map(async (s: SubsystemResponseDto) => {
      const mappings =
        await assetSubsystemMappingsApi.assetSubsystemMappingControllerGetAssetTypesForSubsystem(
          { subsystemId: s.id },
        );
      const assetTypeIds = mappings.map(
        (a: AssetTypeSubsystemMappingResponseDto) => a.assetTypeId,
      );
      return {
        ...s,
        componentClassCodes: [],
        assetTypeIds,
      } as EnrichedSubsystem;
    }),
  );
  return enriched.map(mapSubsystem);
}

async function getComponentClassLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: ComponentClassControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res =
    await componentClassesApi.componentClassControllerFindAll(_params);
  const idToCodeMap = new Map<string, string>();
  (res.data || []).forEach((c: ComponentClassResponseDto) => {
    if (c.id && c.code) idToCodeMap.set(c.id, String(c.code));
  });
  const items = res.data || [];
  const enriched = items.map((c: ComponentClassResponseDto) => {
    return {
      ...c,
      subsystemCodes: [],
    } as EnrichedComponentClass;
  });
  return enriched.map((c: EnrichedComponentClass) =>
    mapComponentClassWithLookup(c, idToCodeMap),
  );
}

async function getEventStateLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: EventStateControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await eventStatesApi.eventStateControllerFindAll(_params);
  return (res.data || []).map(mapEventState);
}

async function getParameterNameLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: ParameterNameControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await parameterNamesApi.parameterNameControllerFindAll(_params);
  return (res.data || []).map(mapParameterName);
}

async function getComponentTypeLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const _params: ComponentTypeControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res: ListComponentTypesResponseDto =
    await componentTypeApi.componentTypeControllerFindAll(_params);
  return (res.data || []).map(mapComponentType);
}

async function getAssetSubsystemComponentClassMappingLookup(): Promise<
  LookupRow[]
> {
  const response =
    (await assetSubsystemComponentClassMappingsApi.assetSubsystemComponentClassControllerFindAll()) as unknown as GetAllMappingsRes;
  return response.data.map(mapAssetSubsystemComponentClass);
}

async function getAssetSubsystemComponentClassComponentTypeMappingLookup(): Promise<
  LookupRow[]
> {
  const res =
    await assetSubsystemComponentClassComponentTypeMappingsApi.assetSubsystemComponentClassComponentTypeControllerFindAll();
  return (res?.data || []).map(mapAssetSubsystemComponentClassComponentType);
}

async function getManufacturerLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const res = await ManufacturerCatalogApi.list({
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  });
  return (res.data || []).map((m) => mapManufacturer(m));
}

async function getAssetModelLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const [modelsRes, assetTypesRes] = await Promise.all([
    AssetModelCatalogApi.list({
      limit: opts?.limit ?? 1000,
      page: opts?.page ?? 1,
      q: opts?.q,
    }),
    assetTypesApi.assetTypeControllerFindAll({ limit: 1000, page: 1 }),
  ]);
  const assetTypeById = new Map<string, { code: string; name: string }>();
  (assetTypesRes.data || []).forEach((a: AssetTypeResponseDto) => {
    assetTypeById.set(a.id, { code: a.code, name: a.name });
  });
  return (modelsRes.data || []).map((m) => mapAssetModel(m, assetTypeById));
}

async function getAssetTypeManufacturerModelLookup(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<LookupRow[]> {
  const [assocRes, assetTypesRes] = await Promise.all([
    AssetTypeManufacturerModelCatalogApi.list({
      limit: opts?.limit ?? 1000,
      page: opts?.page ?? 1,
      q: opts?.q,
    }),
    assetTypesApi.assetTypeControllerFindAll({ limit: 1000, page: 1 }),
  ]);
  const assetTypeCodeById = new Map<string, string>();
  (assetTypesRes.data || []).forEach((a: AssetTypeResponseDto) => {
    assetTypeCodeById.set(a.id, a.code);
  });
  return (assocRes.data || []).map((row) =>
    mapAssetTypeManufacturerModel(row, assetTypeCodeById),
  );
}

const lookupHandlers: Record<
  string,
  (opts?: { limit?: number; page?: number; q?: string }) => Promise<LookupRow[]>
> = {
  'Asset Type': getAssetTypeLookup,
  Subsystem: getSubsystemLookup,
  'Component Class': getComponentClassLookup,
  'Event State': getEventStateLookup,
  'Parameter Name': getParameterNameLookup,
  'Component Type': getComponentTypeLookup,
  Manufacturer: getManufacturerLookup,
  'Asset Model': getAssetModelLookup,
  'Asset Type Manufacturer Model': getAssetTypeManufacturerModelLookup,
  'Asset-Subsystem-ComponentClass Mapping': () =>
    getAssetSubsystemComponentClassMappingLookup(),
  'Asset-Subsystem-ComponentClass-ComponentType Mapping': () =>
    getAssetSubsystemComponentClassComponentTypeMappingLookup(),
};

export async function getLookup(
  configName: string,
  opts?: { limit?: number; page?: number; q?: string },
): Promise<LookupRow[]> {
  const handler = lookupHandlers[configName];
  if (handler) {
    return handler(opts);
  }
  return [];
}

async function getAssetTypeLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: AssetTypeControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await assetTypesApi.assetTypeControllerFindAll(_params);
  const items = res.data || [];
  const enriched = await Promise.all(
    items.map(async (a: AssetTypeResponseDto) => {
      const mappings =
        await assetSubsystemMappingsApi.assetSubsystemMappingControllerGetSubsystemsForAssetType(
          { assetTypeId: a.id },
        );
      const subsystemIds = mappings.map(
        (s: AssetTypeSubsystemMappingResponseDto) => s.subsystemId,
      );
      return { ...a, subsystemIds } as EnrichedAssetType;
    }),
  );
  return { rows: enriched.map(mapAssetType), meta: res.meta };
}

async function getSubsystemLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: SubsystemControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await subsystemsApi.subsystemControllerFindAll(_params);
  const items = res.data || [];
  const enriched = await Promise.all(
    items.map(async (s: SubsystemResponseDto) => {
      const mappings =
        await assetSubsystemMappingsApi.assetSubsystemMappingControllerGetAssetTypesForSubsystem(
          { subsystemId: s.id },
        );
      const assetTypeIds = mappings.map(
        (a: AssetTypeSubsystemMappingResponseDto) => a.assetTypeId,
      );
      return {
        ...s,
        componentClassCodes: [],
        assetTypeIds,
      } as EnrichedSubsystem;
    }),
  );
  return { rows: enriched.map(mapSubsystem), meta: res.meta };
}

async function getComponentClassLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: ComponentClassControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res =
    await componentClassesApi.componentClassControllerFindAll(_params);
  const idToCodeMap = new Map<string, string>();
  (res.data || []).forEach((c: ComponentClassResponseDto) => {
    if (c.id && c.code) idToCodeMap.set(c.id, String(c.code));
  });
  const items = res.data || [];
  const enriched = items.map((c: ComponentClassResponseDto) => {
    return {
      ...c,
      subsystemCodes: [],
    } as EnrichedComponentClass;
  });
  return {
    rows: enriched.map((c: EnrichedComponentClass) =>
      mapComponentClassWithLookup(c, idToCodeMap),
    ),
    meta: res.meta,
  };
}

async function getEventStateLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: EventStateControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await eventStatesApi.eventStateControllerFindAll(_params);
  return {
    rows: (res.data || []).map(mapEventState),
    meta: res.meta,
  };
}

async function getParameterNameLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: ParameterNameControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res = await parameterNamesApi.parameterNameControllerFindAll(_params);
  return {
    rows: (res.data || []).map(mapParameterName),
    meta: res.meta,
  };
}

async function getComponentTypeLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const _params: ComponentTypeControllerFindAllRequest = {
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  };
  const res: ListComponentTypesResponseDto =
    await componentTypeApi.componentTypeControllerFindAll(_params);
  return {
    rows: (res.data || []).map(mapComponentType),
    meta: res.meta,
  };
}

async function getAssetSubsystemComponentClassMappingLookupWithMeta(): Promise<{
  rows: LookupRow[];
  meta?: PaginationMetaDto;
}> {
  const response =
    (await assetSubsystemComponentClassMappingsApi.assetSubsystemComponentClassControllerFindAll()) as unknown as GetAllMappingsRes;
  return {
    rows: response.data.map(mapAssetSubsystemComponentClass),
    meta: undefined,
  };
}

async function getAssetSubsystemComponentClassComponentTypeMappingLookupWithMeta(): Promise<{
  rows: LookupRow[];
  meta?: PaginationMetaDto;
}> {
  const res =
    await assetSubsystemComponentClassComponentTypeMappingsApi.assetSubsystemComponentClassComponentTypeControllerFindAll();
  return {
    rows: (res?.data || []).map(mapAssetSubsystemComponentClassComponentType),
    meta: undefined,
  };
}

async function getManufacturerLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const res = await ManufacturerCatalogApi.list({
    limit: opts?.limit ?? 1000,
    page: opts?.page ?? 1,
    q: opts?.q,
  });
  return {
    rows: (res.data || []).map((m) => mapManufacturer(m)),
    meta: res.meta,
  };
}

async function getAssetModelLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const [modelsRes, assetTypesRes] = await Promise.all([
    AssetModelCatalogApi.list({
      limit: opts?.limit ?? 1000,
      page: opts?.page ?? 1,
      q: opts?.q,
    }),
    assetTypesApi.assetTypeControllerFindAll({ limit: 1000, page: 1 }),
  ]);
  const assetTypeById = new Map<string, { code: string; name: string }>();
  (assetTypesRes.data || []).forEach((a: AssetTypeResponseDto) => {
    assetTypeById.set(a.id, { code: a.code, name: a.name });
  });
  return {
    rows: (modelsRes.data || []).map((m) => mapAssetModel(m, assetTypeById)),
    meta: modelsRes.meta,
  };
}

async function getAssetTypeManufacturerModelLookupWithMeta(opts?: {
  limit?: number;
  page?: number;
  q?: string;
}): Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }> {
  const [assocRes, assetTypesRes] = await Promise.all([
    AssetTypeManufacturerModelCatalogApi.list({
      limit: opts?.limit ?? 1000,
      page: opts?.page ?? 1,
      q: opts?.q,
    }),
    assetTypesApi.assetTypeControllerFindAll({ limit: 1000, page: 1 }),
  ]);
  const assetTypeCodeById = new Map<string, string>();
  (assetTypesRes.data || []).forEach((a: AssetTypeResponseDto) => {
    assetTypeCodeById.set(a.id, a.code);
  });
  return {
    rows: (assocRes.data || []).map((row) =>
      mapAssetTypeManufacturerModel(row, assetTypeCodeById),
    ),
    meta: assocRes.meta,
  };
}

const lookupWithMetaHandlers: Record<
  string,
  (opts?: {
    limit?: number;
    page?: number;
    q?: string;
  }) => Promise<{ rows: LookupRow[]; meta?: PaginationMetaDto }>
> = {
  'Asset Type': getAssetTypeLookupWithMeta,
  Subsystem: getSubsystemLookupWithMeta,
  'Component Class': getComponentClassLookupWithMeta,
  'Event State': getEventStateLookupWithMeta,
  'Parameter Name': getParameterNameLookupWithMeta,
  'Component Type': getComponentTypeLookupWithMeta,
  Manufacturer: getManufacturerLookupWithMeta,
  'Asset Model': getAssetModelLookupWithMeta,
  'Asset Type Manufacturer Model': getAssetTypeManufacturerModelLookupWithMeta,
  'Asset-Subsystem-ComponentClass Mapping': () =>
    getAssetSubsystemComponentClassMappingLookupWithMeta(),
  'Asset-Subsystem-ComponentClass-ComponentType Mapping': () =>
    getAssetSubsystemComponentClassComponentTypeMappingLookupWithMeta(),
};

export async function getLookupWithMeta(
  configName: string,
  opts?: { limit?: number; page?: number; q?: string },
): Promise<{
  rows: LookupRow[];
  meta?: PaginationMetaDto;
}> {
  const handler = lookupWithMetaHandlers[configName];
  if (handler) {
    return handler(opts);
  }
  return { rows: [], meta: undefined };
}

export async function getComponentClassesForAssetSubsystem(
  assetTypeSubsystemId: string,
  operator: 'INCLUDED' | 'NOT_INCLUDED',
  page = 1,
  limit = 1000,
): Promise<{
  rows: LookupRow[];
  meta?: PaginationMetaDto;
}> {
  const _params: ComponentClassControllerFindAllRequest = {
    limit,
    page,
    operator,
    mappingKey: 'ATSSCC',
    tableFilterFieldName: 'assetTypeSubsystemId',
    tableFilterFieldValue: assetTypeSubsystemId,
    mappingId: 'componentClassId',
  };
  const res =
    await componentClassesApi.componentClassControllerFindAll(_params);
  return {
    rows: res.data.map(mapComponentClass),
    meta: res.meta,
  };
}

export async function getComponentTypesForAssetSubsystemComponentClass(
  mappingId: string,
  operator: 'INCLUDED' | 'NOT_INCLUDED',
  page = 1,
  limit = 1000,
): Promise<{
  rows: LookupRow[];
  meta?: PaginationMetaDto;
}> {
  const _params: ComponentTypeControllerFindAllRequest = {
    limit,
    page,
    operator,
    mappingKey: 'ATSSCCSST',
    tableFilterFieldName: 'assetTypeSubsystemComponentClassId',
    tableFilterFieldValue: mappingId,
    mappingId: 'componentTypeId',
  };
  const res = await componentTypeApi.componentTypeControllerFindAll(_params);
  const idToCodeMap = new Map<string, string>();
  res.data.forEach((c) => {
    if (c.id && c.name) idToCodeMap.set(c.id, String(c.name));
  });
  return {
    rows: res.data.map((c: ComponentTypeResponseDto) =>
      mapComponentTypeWithLookup(c),
    ),
    meta: res.meta,
  };
}
